import { SCORING } from '@/config/audio';
import type { AnalysisFrame, ChordAnalysisResult, ChordVerdict } from '@/types/audio';
import { clamp01, median, normalizeRange, stdDev } from '../dsp/stats';
import { bestDetection } from './detect';
import { combineTerms, scoreTargetChord, type BassEvidence, type ChordTarget, type ScoreTerms } from './target';

/**
 * Turns a stream of analysis frames into a verdict about one target chord.
 *
 * Two things are deliberately separated:
 *
 * - the **score** says how well the sound matches the chord;
 * - the **confidence** says whether the score means anything at all.
 *
 * A loud, clean, steady frame that does not match the chord gets a low score
 * and a high confidence: "you played the wrong thing" is a real answer. A quiet
 * or noisy frame gets a low confidence, and the verdict becomes `uncertain` —
 * the app then asks the player to try again instead of inventing feedback.
 */
export class ChordEvaluator {
  private target: ChordTarget;
  private readonly scores: number[] = [];
  private lastTerms: ScoreTerms = { purity: 0, coverage: 0, root: 0, bass: 0 };
  private lastResult: ChordAnalysisResult | null = null;
  private stableFrames = 0;

  constructor(target: ChordTarget) {
    this.target = target;
  }

  setTarget(target: ChordTarget): void {
    if (target.chordId === this.target.chordId) return;
    this.target = target;
    this.reset();
  }

  get currentTarget(): ChordTarget {
    return this.target;
  }

  /** Consecutive frames whose smoothed verdict has been `correct`. */
  get consecutiveStableFrames(): number {
    return this.stableFrames;
  }

  get latest(): ChordAnalysisResult | null {
    return this.lastResult;
  }

  push(frame: AnalysisFrame, bass?: BassEvidence): ChordAnalysisResult {
    const terms = scoreTargetChord(frame.chroma, this.target, bass);
    this.lastTerms = terms;
    const raw = combineTerms(terms);

    this.scores.push(raw);
    if (this.scores.length > SCORING.smoothingFrames) this.scores.shift();

    const smoothed = median(this.scores);
    const confidence = this.computeConfidence(frame, smoothed);
    let status = verdictFor(smoothed, confidence);

    // Octave-specific note evidence outranks the pitch-class score for the
    // top verdict: a single string's partials can satisfy every pitch class
    // of a triad (measured: one open A string scored 0.93 against the A
    // chord), but cannot produce the voicing's other notes in their own
    // octaves. "Correct" means the chord was played, not that its pitch
    // classes were somehow present.
    const noteCoverage = frame.noteCoverage;
    if (
      status === 'correct' &&
      noteCoverage !== undefined &&
      noteCoverage < SCORING.noteCoverage.minForCorrect
    ) {
      status = 'almost';
    }

    if (status === 'correct') this.stableFrames++;
    else this.stableFrames = 0;

    const detection = bestDetection(frame.chroma);
    const result: ChordAnalysisResult = {
      targetScore: smoothed,
      confidence,
      status,
      terms,
      ...(noteCoverage !== undefined ? { noteCoverage } : {}),
      ...(detection ? { detectedChord: detection.name } : {}),
    };
    this.lastResult = result;
    return result;
  }

  private computeConfidence(frame: AnalysisFrame, smoothed: number): number {
    const c = SCORING.confidence;
    const snrTerm = normalizeRange(frame.snrDb, c.zeroSnrDb, c.fullSnrDb);
    const tonalTerm = normalizeRange(frame.tonalness, 0, c.fullTonalness);

    // Stability: a score that is jumping around means the analyser is not
    // seeing one steady thing, so it should not be trusted.
    const spread = this.scores.length >= 3 ? stdDev(this.scores) : c.unstableStdDev;
    const stabilityTerm = clamp01(1 - spread / (c.unstableStdDev * 2));

    let confidence =
      snrTerm * c.weights.snr + tonalTerm * c.weights.tonalness + stabilityTerm * c.weights.stability;

    if (frame.clipped) confidence *= c.clippedPenalty;

    // Very few frames so far: we simply have not heard enough yet.
    if (this.scores.length < 2) confidence *= 0.6;

    // A confidently *bad* match is still confident, but only if the signal is
    // strong; a near-silent frame must never produce "wrong".
    void smoothed;
    return clamp01(confidence);
  }

  get terms(): ScoreTerms {
    return this.lastTerms;
  }

  reset(): void {
    this.scores.length = 0;
    this.stableFrames = 0;
    this.lastResult = null;
    this.lastTerms = { purity: 0, coverage: 0, root: 0, bass: 0 };
  }
}

export function verdictFor(score: number, confidence: number): ChordVerdict {
  if (confidence < SCORING.uncertainConfidence) return 'uncertain';
  if (score >= SCORING.correctScore) return 'correct';
  if (score >= SCORING.almostScore) return 'almost';
  return 'wrong';
}
